/**
 * The atlas: the expedition concept drawn with the restraint of a graduate
 * seminar.
 *
 * One vertical map on warm paper. Each week is a region opened by an
 * editorial header (small-caps kicker, serif title, hairline progress rule)
 * rather than a coloured banner. Stations are thin-ringed circles joined by
 * a fine route line; the route fills in as work is completed. Optional items
 * sit in a "Going further" panel at the end of the region. Regions are
 * separated by hairline horizons and faint contour lines, with a little
 * line-art scenery and one of the three plush mascots in the margins. Weeks
 * after the current one fade and lock. The canvas is the phone canvas
 * ({@link CANVAS_WIDTH}) stretched sideways on wider screens, up to
 * {@link MAX_CANVAS_WIDTH}, and scaled down on narrower ones, so every
 * screen sees the same composition.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  FaBolt,
  FaBookOpen,
  FaCheck,
  FaGem,
  FaLock,
  FaPython,
  FaRocket,
  FaUsers,
} from 'react-icons/fa';
import styled, {keyframes} from 'styled-components';
import type {PlushState} from '../mascot/PaiPlush';
import {
  InkCairn,
  InkCompass,
  InkPine,
  InkReeds,
  InkShrub,
  InkTree,
  type AtlasPropProps,
} from './atlasScenery';
import type {
  ExpeditionItem,
  ExpeditionItemKind,
  ExpeditionWeek,
} from './expeditionModel';
import {findNextItem, itemTooltip} from './expeditionModel';
import {
  CheckIcon,
  CodeIcon,
  GemIcon,
  LearnIcon,
  LinkIcon,
  LockIcon,
  SectionIcon,
} from './journeyIcons';
import {mix} from './mapColor';
import {
  meanderPath,
  meanderX,
  roadX,
  type Meander,
  type MeanderSkip,
  type RoadAnchor,
} from './meander';
import {
  HtmlDecorations,
  mapCanvasFilterCss,
  svgDecorations,
  type LandBox,
  type RoadmapLayoutInfo,
} from './lookAndFeel/decorations';
import {
  DEFAULT_LOOK_AND_FEEL,
  weekLook,
  type LookAndFeel,
  type Placement,
} from './lookAndFeel/lookAndFeelSchema';
import {FINALE_HEIGHT, MapFinale} from './MapFinale';
import {DEFAULT_PALETTE} from './palettes';
import {allDone, countDone} from './roadmapPrototypeShared';
import {useMapLinkCallout} from './useMapLinkCallout';

/** The phone canvas width; wider screens stretch it sideways. */
const CANVAS_WIDTH = 430;
/** The widest the canvas stretches on a large screen. */
const MAX_CANVAS_WIDTH = 640;
/** Containers narrower than this get the canvas edge to edge. */
const FULL_BLEED_BELOW = CANVAS_WIDTH + 24;
/** How far stations swing left and right of the centre line. */
const AMPLITUDE = 48;
const STATION_SIZE = 54;
const GEM_SIZE = 40;
const GEM_PITCH = 104;
const CAPTION_WIDTH = 170;
/** Room a caption takes beneath its station. */
const UNDER_CAPTION = 74;
const GEM_CAPTION_HEIGHT = 50;
const MARGIN_X = 18;
const HEADER_HEIGHT = 112;
const HEADER_TO_FIRST_STATION = 78;
const PANEL_MIN_WIDTH = 240;
const PANEL_LABEL_HEIGHT = 20;
const PANEL_PADDING = 18;
const REGION_TAIL = 64;
const HORIZON_AMPLITUDE = 10;
/** Extra room above the current station so its label clears the one before. */
const NEXT_ROOM = 26;

const PAPER = '#f7f3ec';
/** The plain variant's card: a neutral grey that reads on light and dark pages. */
const PLAIN_CARD = '#eceae6';
const INK = '#1f1a17';
const INK_SOFT = '#6b625b';
const HAIRLINE = 'rgba(31, 26, 23, 0.12)';
const SERIF = "'Source Serif 4', 'Iowan Old Style', Georgia, serif";
const SANS = "'Source Sans 3', system-ui, sans-serif";

const LOCKED_ACCENT = '#a49c93';

interface Point {
  x: number;
  y: number;
}

interface StationLayout extends Point {
  item: ExpeditionItem;
}

interface PanelLayout {
  left: number;
  top: number;
  width: number;
  height: number;
  gems: StationLayout[];
}

/** The numbers every layout step reads. */
interface Geometry {
  width: number;
  centerX: number;
  /** How far stations swing left and right of the centre line. */
  amplitude: number;
  /** Horizontal room outside the stations and their captions, per side. */
  marginWidth: number;
  /** Vertical distance between consecutive stations. */
  stepY: number;
  /** Horizontal inset of headers, panels and the finale. */
  marginX: number;
  /** Width of a caption. */
  captionWidth: number;
  /** The route's shape on this canvas. */
  meander: Meander;
}

interface RegionLayout {
  g: Geometry;
  /** The region's title as shown: the week's, or the course's override. */
  title: string;
  /** Stations moved off the meander, which the route detours through. */
  anchors: RoadAnchor[];
  week: ExpeditionWeek;
  index: number;
  accent: string;
  isLocked: boolean;
  top: number;
  bottom: number;
  headerTop: number;
  headerHeight: number;
  stations: StationLayout[];
  panel?: PanelLayout;
}

/** The closing card after the last region. */
interface FinaleLayout {
  top: number;
  height: number;
}

interface AtlasLayout {
  g: Geometry;
  /** The route's shape, with the phase frozen between regions. */
  meander: Meander;
  height: number;
  regions: RegionLayout[];
  finale: FinaleLayout;
}

/** Height of the banner header the plain variants use. */
const BANNER_HEIGHT = 96;

/**
 * The layout geometry for a canvas width. The swing of the road scales with
 * the width so a wider canvas is the phone canvas stretched sideways.
 *
 * @param width - Canvas width in pixels, between {@link CANVAS_WIDTH} and
 *     {@link MAX_CANVAS_WIDTH}.
 * @return The geometry.
 */
function geometryFor(width: number): Geometry {
  const amplitude = (AMPLITUDE * width) / CANVAS_WIDTH;
  return {
    width,
    centerX: width / 2,
    amplitude,
    marginWidth: width / 2 - amplitude - CAPTION_WIDTH / 2,
    stepY: 184,
    marginX: MARGIN_X,
    captionWidth: CAPTION_WIDTH,
    meander: {centerX: width / 2, amplitude},
  };
}

/**
 * Lays the map out in pixel space.
 *
 * @param args.weeks - Weeks, top to bottom.
 * @param args.nextItemId - The station that carries the "up next" label,
 *     which needs extra room above it. When undefined, none does.
 * @param args.g - The geometry for the available width.
 * @param args.isPlain - Draw every region in the one plain accent.
 * @return Positioned regions and the total canvas height.
 */
function layoutAtlas({
  weeks,
  nextItemId,
  g,
  isPlain,
  lookAndFeel,
}: {
  weeks: readonly ExpeditionWeek[];
  nextItemId: string | undefined;
  g: Geometry;
  isPlain: boolean;
  lookAndFeel: LookAndFeel;
}): AtlasLayout {
  const regions: RegionLayout[] = [];
  let cursor = 0;
  // The wave does not advance between one region's last station and the
  // next's first, so the route resumes where it stopped.
  const skips: MeanderSkip[] = [];
  const meander: Meander = {...g.meander, skips};
  let previousRouteTo: number | undefined;

  weeks.forEach((week, index) => {
    const top = cursor;
    const headerTop = top + 28;
    const headerHeight = isPlain ? BANNER_HEIGHT + 14 : HEADER_HEIGHT - 8;
    let y = headerTop + headerHeight + HEADER_TO_FIRST_STATION;
    const routeFrom = y - STATION_SIZE / 2 - 6;
    if (previousRouteTo !== undefined && routeFrom > previousRouteTo) {
      skips.push({from: previousRouteTo, to: routeFrom});
    }

    const look = weekLook(lookAndFeel, week.id);
    const stations: StationLayout[] = [];
    let contentBottom = y - STATION_SIZE / 2;
    const slots: Array<{item: ExpeditionItem; y: number}> = [];
    week.main.forEach(item => {
      if (item.id === nextItemId) {
        y += NEXT_ROOM + 22;
      }
      const nudge = look.stones[item.id]?.dy ?? 0;
      slots.push({item, y: y + nudge});
      contentBottom = Math.max(
        contentBottom,
        y + nudge + STATION_SIZE / 2 + UNDER_CAPTION,
      );
      y += g.stepY;
    });
    // Every coin is an anchor; only moved ones pull, so the road's offset
    // eases from coin to coin and returns to the meander at unmoved ones.
    const anchors: RoadAnchor[] = look.stones
      ? slots.map(({item, y: slotY}) => {
          const moved = look.stones[item.id];
          return {
            y: slotY,
            dx: moved ? moved.x * g.width - meanderX(slotY, meander) : 0,
            reach: g.stepY * 0.9,
          };
        })
      : [];
    slots.forEach(({item, y: slotY}) => {
      stations.push({item, x: roadX(slotY, meander, anchors), y: slotY});
    });
    if (stations.length > 0) {
      previousRouteTo =
        stations[stations.length - 1].y + STATION_SIZE / 2 + UNDER_CAPTION;
    }

    let panel: PanelLayout | undefined;
    if (week.goingFurther.length > 0) {
      const count = week.goingFurther.length;
      const maxWidth = g.width - g.marginX * 2;
      const perRow = Math.max(
        1,
        Math.floor((maxWidth - PANEL_PADDING * 2) / GEM_PITCH),
      );
      const rows = Math.ceil(count / perRow);
      const width = Math.min(
        maxWidth,
        Math.max(
          PANEL_MIN_WIDTH,
          Math.min(count, perRow) * GEM_PITCH + PANEL_PADDING * 2,
        ),
      );
      const rowHeight = GEM_SIZE + GEM_CAPTION_HEIGHT;
      const height =
        PANEL_PADDING +
        PANEL_LABEL_HEIGHT +
        16 +
        rows * rowHeight +
        PANEL_PADDING -
        6;
      const left = g.centerX - width / 2;
      const panelTop = contentBottom + 44;
      const gems = week.goingFurther.map((item, gemIndex) => {
        const row = Math.floor(gemIndex / perRow);
        const column = gemIndex % perRow;
        const inRow = Math.min(perRow, count - row * perRow);
        const rowLeft = g.centerX - (inRow * GEM_PITCH) / 2;
        return {
          item,
          x: rowLeft + GEM_PITCH * column + GEM_PITCH / 2,
          y:
            panelTop +
            PANEL_PADDING +
            PANEL_LABEL_HEIGHT +
            16 +
            row * rowHeight +
            GEM_SIZE / 2,
        };
      });
      panel = {left, top: panelTop, width, height, gems};
      contentBottom = panelTop + height;
    }

    const bottom = contentBottom + REGION_TAIL;
    regions.push({
      g,
      title: look.title?.trim() || week.title,
      anchors,
      week,
      index,
      accent: week.isLocked
        ? LOCKED_ACCENT
        : (look.accent ??
          (isPlain
            ? DEFAULT_PALETTE.plainAccent
            : DEFAULT_PALETTE.lands[index % DEFAULT_PALETTE.lands.length]
                .accent)),
      isLocked: week.isLocked,
      top,
      bottom,
      headerTop,
      headerHeight,
      stations,
      panel,
    });
    cursor = bottom;
  });

  const finale: FinaleLayout = {top: cursor + 8, height: FINALE_HEIGHT};
  const height = lookAndFeel.showFinale
    ? finale.top + finale.height + 32
    : cursor + 24;
  return {g, meander, height, regions, finale};
}

/**
 * Automatic placements the course has adopted or removed are not drawn.
 *
 * @param placements - Automatic placements.
 * @param hiddenAuto - Ids the week hides.
 * @return The placements still to draw.
 */
function withoutHidden(
  placements: Placement[],
  hiddenAuto: readonly string[],
): Placement[] {
  return hiddenAuto.length === 0
    ? placements
    : placements.filter(placement => !hiddenAuto.includes(placement.id));
}

/**
 * The public shape of a region, for decorations and for editors overlaying
 * the map.
 *
 * @param region - The laid-out region.
 * @return Its box and colours.
 */
function regionBox(region: RegionLayout, autoPlacements: Placement[]): LandBox {
  return {
    id: region.week.id,
    top: region.top,
    contentTop: region.headerTop + region.headerHeight + 8,
    bottom: region.bottom,
    accent: region.accent,
    ground: PAPER,
    title: region.title,
    stones: region.stations.map(station => ({
      id: station.item.id,
      x: station.x,
      y: station.y,
    })),
    autoPlacements,
  };
}

/**
 * The plush the map seats in a region's margin, as a placement.
 *
 * @param region - The region.
 * @param state - The expression to show.
 * @return One placement, or none when the margin has no room.
 */
function autoPlushPlacement(
  region: RegionLayout,
  state: PlushState,
): Placement[] {
  const spot = plushSpot(region);
  if (!spot) {
    return [];
  }
  const top = region.headerTop + region.headerHeight + 8;
  const widget = ['ollie', 'plush-pi', 'beanie'][region.index % 3];
  return [
    {
      id: `auto-${region.week.id}-plush`,
      widget,
      x: spot.x / region.g.width,
      y: Math.min(
        1,
        Math.max(0, (spot.y + 4 - top) / Math.max(1, region.bottom - top)),
      ),
      scale: widget === 'beanie' ? 0.95 : 1,
      flip: false,
      variant: state,
    },
  ];
}

/**
 * The expression a region's plush wears for a viewer's progress.
 *
 * @param region - The region.
 * @param completed - Completed item ids.
 * @param nextItemId - The viewer's next item, if any.
 * @return The plush state.
 */
function plushStateFor(
  region: RegionLayout,
  completed: ReadonlySet<string>,
  nextItemId: string | undefined,
): PlushState {
  if (region.isLocked) {
    return 'sleepy';
  }
  if (allDone(region.week.main, completed)) {
    return 'happy';
  }
  return region.stations.some(s => s.item.id === nextItemId)
    ? 'surprised'
    : 'idle';
}

/**
 * The horizon between a region and the next: a gentle wave across the width.
 *
 * @param region - The region whose bottom edge this is.
 * @return An SVG path string, open (a stroke, not a fill).
 */
function horizonPath(region: RegionLayout): string {
  const {bottom, g} = region;
  const a = HORIZON_AMPLITUDE;
  const phase = region.index % 2 === 0 ? 1 : -1;
  const w = g.width;
  return [
    `M 0 ${bottom + phase * a * 0.4}`,
    `C ${w * 0.18} ${bottom - phase * a * 1.4}, ${w * 0.36} ${bottom + phase * a * 1.4}, ${w * 0.52} ${bottom}`,
    `C ${w * 0.68} ${bottom - phase * a * 1.4}, ${w * 0.84} ${bottom + phase * a * 1.2}, ${w} ${bottom - phase * a * 0.3}`,
  ].join(' ');
}

/**
 * Faint contour lines across a region, like elevation lines on a survey.
 *
 * @param region - The region to draw within.
 * @return Open SVG paths with stable keys, top to bottom.
 */
function contourPaths(region: RegionLayout): Array<{key: string; d: string}> {
  const {g} = region;
  const w = g.width;
  const contentTop = region.headerTop + region.headerHeight + 10;
  const span = region.bottom - contentTop;
  const count = Math.max(3, Math.min(6, Math.round(span / 120)));
  const paths: Array<{key: string; d: string}> = [];
  for (let i = 0; i < count; i += 1) {
    const yBase = contentTop + (span * (i + 0.6)) / count;
    const drift = (region.index % 2 === 0 ? 1 : -1) * (i % 2 === 0 ? 1 : -1);
    const a = 14 + (i % 3) * 6;
    paths.push({
      key: `c${Math.round(yBase)}`,
      d: [
        `M -10 ${yBase}`,
        `C ${w * 0.2} ${yBase - drift * a}, ${w * 0.35} ${yBase + drift * a * 1.3}, ${w * 0.5} ${yBase + drift * a * 0.2}`,
        `C ${w * 0.65} ${yBase - drift * a * 1.1}, ${w * 0.82} ${yBase + drift * a * 0.9}, ${w + 10} ${yBase - drift * a * 0.4}`,
      ].join(' '),
    });
  }
  return paths;
}

/**
 * Ground lines in a margin that no side caption or panel occupies.
 *
 * @param region - The region to search.
 * @param side - Which margin: -1 for left, 1 for right.
 * @return Candidate ground y positions, top to bottom.
 */
function freeGroundLines(region: RegionLayout, side: 1 | -1): number[] {
  const contentTop = region.headerTop + region.headerHeight + 16;
  const contentBottom = region.bottom - 14;
  const busy: Array<[number, number]> = region.stations
    .filter(station => (station.x >= region.g.centerX ? 1 : -1) === side)
    .map(station => [station.y - 40, station.y + 42]);
  if (region.panel) {
    busy.push([
      region.panel.top - 10,
      region.panel.top + region.panel.height + 10,
    ]);
  }
  const lines: number[] = [];
  for (let y = contentTop + 64; y <= contentBottom; y += 20) {
    const isFree = busy.every(([from, to]) => y < from || y - 64 > to);
    if (isFree) {
      lines.push(y);
    }
  }
  return lines;
}

/** Minimum vertical distance between two props sharing a margin. */
const PROP_MIN_GAP = 104;

/**
 * Picks well-spread ground lines from the free ones, at least
 * {@link PROP_MIN_GAP} apart.
 *
 * @param lines - Free ground lines, top to bottom.
 * @param count - How many to pick.
 * @return Up to `count` lines, spread across the range.
 */
function spread(lines: number[], count: number): number[] {
  const thinned: number[] = [];
  for (const y of lines) {
    if (
      thinned.length === 0 ||
      y - thinned[thinned.length - 1] >= PROP_MIN_GAP
    ) {
      thinned.push(y);
    }
  }
  if (thinned.length === 0 || count === 0) {
    return [];
  }
  if (thinned.length <= count) {
    return thinned;
  }
  return Array.from(
    {length: count},
    (_, index) =>
      thinned[Math.round(((index + 0.5) * (thinned.length - 1)) / count)],
  );
}

/**
 * Which margin a region's plush sits in: whichever has more room. Ties
 * alternate.
 *
 * @param region - The region.
 * @return -1 for the left margin, 1 for the right.
 */
function plushSideOf(region: RegionLayout): 1 | -1 {
  const left = spread(freeGroundLines(region, -1), 3).length;
  const right = spread(freeGroundLines(region, 1), 3).length;
  if (left === right) {
    return region.index % 2 === 0 ? 1 : -1;
  }
  return left > right ? -1 : 1;
}

/**
 * How a margin's free ground lines are shared between scenery and the plush.
 *
 * @param region - The region.
 * @param side - Which margin: -1 for left, 1 for right.
 * @param plushSide - The margin the plush sits in.
 * @return Scenery lines and, on the plush's side, the plush's line.
 */
function marginSlots(
  region: RegionLayout,
  side: 1 | -1,
  plushSide: 1 | -1,
): {sceneryLines: number[]; plushLine?: number} {
  const lines = spread(freeGroundLines(region, side), 3);
  if (side !== plushSide || lines.length === 0) {
    return {sceneryLines: lines};
  }
  return {
    sceneryLines: lines.slice(0, -1),
    plushLine: lines[lines.length - 1],
  };
}

/**
 * The mascots the map seats on its own: one in the margin and, on the region
 * holding the next station, Beanie beside that station.
 *
 * @param region - The region.
 * @param completed - Ids of completed items.
 * @param nextItemId - The station up next, if any.
 * @return Placements with ids stable across renders.
 */
function autoMascotPlacements(
  region: RegionLayout,
  completed: ReadonlySet<string>,
  nextItemId: string | undefined,
): Placement[] {
  return [
    ...autoPlushPlacement(region, plushStateFor(region, completed, nextItemId)),
    ...autoCompanionPlacement(region, nextItemId),
  ];
}

/**
 * Beanie beside the next station, on the side towards the centre line.
 *
 * @param region - The region.
 * @param nextItemId - The station up next, if any.
 * @return One placement when the next station is on this region, else none.
 */
function autoCompanionPlacement(
  region: RegionLayout,
  nextItemId: string | undefined,
): Placement[] {
  const station = region.stations.find(
    candidate => candidate.item.id === nextItemId,
  );
  if (!station) {
    return [];
  }
  const side = station.x >= region.g.centerX ? 1 : -1;
  const top = region.headerTop + region.headerHeight + 8;
  const y = station.y + STATION_SIZE / 2 + 4;
  return [
    {
      id: `auto-${region.week.id}-companion`,
      widget: 'beanie',
      x: (station.x - side * (STATION_SIZE / 2 + 36)) / region.g.width,
      y: Math.min(1, Math.max(0, (y - top) / Math.max(1, region.bottom - top))),
      scale: 0.7,
      flip: false,
      variant: 'happy',
    },
  ];
}

/**
 * Where a region's plush stands, or undefined when the margin has no room.
 *
 * @param region - The region.
 * @return The plush's base point.
 */
function plushSpot(region: RegionLayout): Point | undefined {
  if (region.g.marginWidth < 60) {
    return undefined;
  }
  const side = plushSideOf(region);
  const {plushLine} = marginSlots(region, side, side);
  if (plushLine === undefined) {
    return undefined;
  }
  const inset = region.g.marginWidth / 2 + 4;
  return {x: side === 1 ? region.g.width - inset : inset, y: plushLine};
}

/**
 * Line-art scenery for one region, in the margins where nothing else is.
 *
 * @param region - The region to decorate.
 * @param plushSide - The margin reserved for the plush.
 * @return Scenery fragments, or null when the margins are too narrow.
 */
function regionScenery(region: RegionLayout, plushSide: 1 | -1): ReactNode {
  const {g} = region;
  if (g.marginWidth < 60) {
    return null;
  }
  const base: AtlasPropProps = {x: 0, y: 0, ink: region.accent};
  const variant = region.index % 3;
  const left = marginSlots(region, -1, plushSide).sceneryLines;
  const right = marginSlots(region, 1, plushSide).sceneryLines;
  const L = g.marginWidth / 2;
  const R = g.width - L;
  const sc = 0.85;

  const leftProps: ReactNode[] = [
    variant === 1 ? (
      <InkPine {...base} x={L} scale={sc} />
    ) : (
      <InkTree {...base} x={L} scale={sc} />
    ),
    variant === 2 ? (
      <InkReeds {...base} x={L + 8} scale={sc} />
    ) : (
      <InkShrub {...base} x={L + 4} scale={sc} />
    ),
    <InkCairn key="lc" {...base} x={L} scale={0.9 * sc} />,
  ];
  const rightProps: ReactNode[] = [
    region.index === 0 ? (
      <InkCompass {...base} x={R} scale={sc} />
    ) : variant === 0 ? (
      <InkPine {...base} x={R} scale={1.05 * sc} />
    ) : (
      <InkTree {...base} x={R} scale={sc} />
    ),
    variant === 1 ? (
      <InkReeds {...base} x={R - 6} scale={sc} />
    ) : (
      <InkShrub {...base} x={R - 4} scale={sc} />
    ),
    variant === 2 ? (
      <InkPine {...base} x={R} scale={0.9 * sc} />
    ) : (
      <InkCairn {...base} x={R} scale={0.9 * sc} />
    ),
  ];

  return (
    <g opacity={region.isLocked ? 0.35 : 0.55}>
      {left.map((y, index) => (
        <g key={`l-${y}`} transform={`translate(0 ${y})`}>
          {leftProps[index]}
        </g>
      ))}
      {right.map((y, index) => (
        <g key={`r-${y}`} transform={`translate(0 ${y})`}>
          {rightProps[index]}
        </g>
      ))}
    </g>
  );
}

const breathe = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  70% { transform: scale(1.22); opacity: 0; }
  100% { transform: scale(1.22); opacity: 0; }
`;

const Scroller = styled.div`
  overflow-x: hidden;
  padding-bottom: 1rem;
  /* Runs edge to edge inside the course page's narrow-screen gutter. */
  margin-left: calc(-1 * var(--course-page-gutter, 0px));
  margin-right: calc(-1 * var(--course-page-gutter, 0px));
`;

/** The canvas's footprint on screen: its logical size times the scale. */
const Frame = styled.div<{
  $width: number;
  $height: number;
  $isPlain: boolean;
  $isFullBleed: boolean;
}>`
  position: relative;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  margin: 0 auto;
  border-radius: ${({$isFullBleed}) => ($isFullBleed ? '0' : '18px')};
  overflow: hidden;
  box-shadow: ${({$isPlain, $isFullBleed}) =>
    $isFullBleed
      ? 'none'
      : $isPlain
        ? '0 0 0 1px rgba(31, 26, 23, 0.06), 0 12px 32px rgba(0, 0, 0, 0.14)'
        : '0 0 0 1px rgba(31, 26, 23, 0.08), 0 20px 50px rgba(31, 26, 23, 0.08)'};
`;

const Canvas = styled.div<{
  $width: number;
  $height: number;
  $isPlain: boolean;
  $scale: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  transform: scale(${({$scale}) => $scale});
  transform-origin: top left;
  background: ${({$isPlain}) => ($isPlain ? PLAIN_CARD : PAPER)};
  font-family: ${SANS};
  color: ${INK};
  ${mapCanvasFilterCss}
`;

const World = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Header = styled.header<{$top: number; $height: number; $inset: number}>`
  position: absolute;
  left: ${({$inset}) => $inset}px;
  right: ${({$inset}) => $inset}px;
  top: ${({$top}) => $top}px;
  height: ${({$height}) => $height}px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
`;

const Kicker = styled.div<{$color: string}>`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({$color}) => $color};
  margin-bottom: 6px;
`;

const Title = styled.h3<{$isLocked: boolean}>`
  margin: 0;
  font-family: ${SERIF};
  font-size: clamp(1.5rem, 4.5vw, 1.85rem);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: ${({$isLocked}) => ($isLocked ? INK_SOFT : INK)};
`;

const Status = styled.div<{$color: string}>`
  flex: none;
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  font-family: ${SERIF};
  font-size: 1.1rem;
  color: ${({$color}) => $color};
  white-space: nowrap;

  small {
    font-family: ${SANS};
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${INK_SOFT};
  }

  svg {
    width: 14px;
    height: 14px;
    align-self: center;
  }
`;

const Rule = styled.div<{$accent: string; $fraction: number}>`
  position: relative;
  height: 2px;
  background: ${HAIRLINE};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({$fraction}) => Math.round($fraction * 100)}%;
    background: ${({$accent}) => $accent};
    transition: width 0.4s ease;
  }
`;

const Numeral = styled.div<{$top: number; $color: string; $inset: number}>`
  position: absolute;
  right: ${({$inset}) => $inset - 6}px;
  top: ${({$top}) => $top - 18}px;
  font-family: ${SERIF};
  font-size: 6.5rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.04em;
  color: ${({$color}) => $color};
  opacity: 0.08;
  pointer-events: none;
  user-select: none;
`;

/** The plain variants' header: the expedition's banner, in one accent. */
const Banner = styled.header<{
  $top: number;
  $height: number;
  $inset: number;
  $accent: string;
}>`
  position: absolute;
  left: ${({$inset}) => $inset}px;
  right: ${({$inset}) => $inset}px;
  top: ${({$top}) => $top}px;
  height: ${({$height}) => $height}px;
  box-sizing: border-box;
  border-radius: 18px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #fff;

  > div {
    min-width: 0;
  }
  background: linear-gradient(
    135deg,
    ${({$accent}) => mix($accent, '#ffffff', 0.1)} 0%,
    ${({$accent}) => $accent} 55%,
    ${({$accent}) => mix($accent, '#000000', 0.12)} 100%
  );
  box-shadow:
    0 3px 0 ${({$accent}) => mix($accent, '#000000', 0.3)},
    0 14px 28px rgba(0, 0, 0, 0.16);
`;

const BannerKicker = styled.div`
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.85;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannerTitle = styled.h3`
  margin: 0;
  font-family: ${SERIF};
  font-size: clamp(1.2rem, 5.2vw, 1.7rem);
  font-weight: 600;
  line-height: 1.1;
  color: #fff;
`;

const BannerPill = styled.div`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  background: rgba(255, 255, 255, 0.18);
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StationAnchor = styled.div<{$x: number; $y: number}>`
  position: absolute;
  left: ${({$x}) => $x - STATION_SIZE / 2}px;
  top: ${({$y}) => $y - STATION_SIZE / 2}px;
  width: ${STATION_SIZE}px;
  height: ${STATION_SIZE}px;
`;

const Halo = styled.span<{$color: string}>`
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 1.5px solid ${({$color}) => $color};
  animation: ${breathe} 2.2s ease-out infinite;
  pointer-events: none;
`;

type StationTone = 'done' | 'next' | 'open' | 'locked';

const StationFace = styled.button<{
  $accent: string;
  $tone: StationTone;
  $size: number;
}>`
  position: absolute;
  inset: 0;
  padding: 0;
  border-radius: 50%;
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  background: ${({$accent, $tone}) =>
    $tone === 'done'
      ? $accent
      : $tone === 'locked'
        ? 'rgba(255, 255, 255, 0.45)'
        : '#ffffff'};
  color: ${({$accent, $tone}) =>
    $tone === 'done'
      ? '#ffffff'
      : $tone === 'locked'
        ? LOCKED_ACCENT
        : $accent};
  border: ${({$accent, $tone}) =>
    $tone === 'done'
      ? `1.5px solid ${$accent}`
      : $tone === 'next'
        ? `2px solid ${$accent}`
        : $tone === 'locked'
          ? `1.5px solid ${mix(LOCKED_ACCENT, PAPER, 0.4)}`
          : `1.5px solid ${mix($accent, '#ffffff', 0.35)}`};
  box-shadow: ${({$tone}) =>
    $tone === 'locked'
      ? 'none'
      : '0 1px 2px rgba(31, 26, 23, 0.08), 0 8px 20px rgba(31, 26, 23, 0.08)'};
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  svg {
    width: ${({$size}) => Math.round($size * 0.42)}px;
    height: ${({$size}) => Math.round($size * 0.42)}px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow:
      0 2px 4px rgba(31, 26, 23, 0.08),
      0 14px 28px rgba(31, 26, 23, 0.12);
  }

  &:focus-visible {
    outline: 2px solid ${({$accent}) => $accent};
    outline-offset: 4px;
  }
`;

/** The text style captions share. */
const CaptionText = styled.div<{
  $x: number;
  $y: number;
  $side: 1 | -1;
  $isLocked: boolean;
}>`
  position: absolute;
  top: ${({$y}) => $y}px;
  left: ${({$x, $side}) => ($side === 1 ? $x : $x - CAPTION_WIDTH)}px;
  width: ${CAPTION_WIDTH}px;
  transform: translateY(-50%);
  text-align: ${({$side}) => ($side === 1 ? 'left' : 'right')};
  line-height: 1.25;
  color: ${({$isLocked}) => ($isLocked ? INK_SOFT : INK)};
  pointer-events: none;

  small {
    display: block;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${INK_SOFT};
    margin-bottom: 3px;
  }

  strong {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
  }
`;

/** A station's name beneath it. */
const CenterCaption = styled(CaptionText)<{$width: number}>`
  left: ${({$x, $width}) => $x - $width / 2}px;
  width: ${({$width}) => $width}px;
  transform: none;
  text-align: center;

  strong {
    font-size: 1rem;
  }
`;

const NextLabel = styled.button<{$x: number; $y: number; $accent: string}>`
  position: absolute;
  left: ${({$x}) => $x}px;
  top: ${({$y}) => $y}px;
  transform: translate(-50%, -100%);
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  background: ${({$accent}) => $accent};
  color: #fff;
  font: inherit;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(31, 26, 23, 0.14);
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -5px;
    width: 10px;
    height: 10px;
    background: ${({$accent}) => $accent};
    transform: translateX(-50%) rotate(45deg);
  }
`;

const Panel = styled.section<{
  $left: number;
  $top: number;
  $width: number;
  $height: number;
  $isLocked: boolean;
}>`
  position: absolute;
  left: ${({$left}) => $left}px;
  top: ${({$top}) => $top}px;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  box-sizing: border-box;
  border-radius: 14px;
  background: rgba(255, 255, 255, ${({$isLocked}) => ($isLocked ? 0.35 : 0.6)});
  border: 1px solid ${HAIRLINE};
`;

const PanelLabel = styled.div<{$color: string}>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${PANEL_PADDING}px;
  height: ${PANEL_LABEL_HEIGHT}px;
  text-align: center;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({$color}) => $color};
`;

const GemCaption = styled.div<{$x: number; $y: number; $isLocked: boolean}>`
  position: absolute;
  left: ${({$x}) => $x - (GEM_PITCH - 10) / 2}px;
  top: ${({$y}) => $y}px;
  width: ${GEM_PITCH - 10}px;
  text-align: center;
  line-height: 1.25;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({$isLocked}) => ($isLocked ? INK_SOFT : INK)};
  pointer-events: none;
`;

/** The glyphs a station can show, by item kind and by state. */
interface GlyphSet {
  kind: Record<ExpeditionItemKind, ReactNode>;
  done: ReactNode;
  locked: ReactNode;
  gem: ReactNode;
}

const CARVED_GLYPHS: GlyphSet = {
  kind: {
    learn: <LearnIcon />,
    code: <CodeIcon />,
    math: <FaRocket />,
    section: <SectionIcon />,
    link: <LinkIcon />,
    check: <CheckIcon />,
  },
  done: <CheckIcon />,
  locked: <LockIcon />,
  gem: <GemIcon />,
};

/** The classic list's Font Awesome glyphs (see `components/completion/TodoIcon`). */
const FLAT_GLYPHS: GlyphSet = {
  kind: {
    learn: <FaBookOpen />,
    code: <FaPython />,
    math: <FaRocket />,
    section: <FaUsers />,
    link: <FaRocket />,
    check: <FaBolt />,
  },
  done: <FaCheck />,
  locked: <FaLock />,
  gem: <FaGem />,
};

/**
 * Two-digit watermark numeral for a week, read from its label ("Week 3"
 * gives "03"). Labels without a number, such as an application week, get none.
 *
 * @param label - The week's short label.
 * @return The padded numeral, or undefined when the label has no number.
 */
function numeral(label: string): string | undefined {
  const match = /\d+/.exec(label);
  return match ? match[0].padStart(2, '0') : undefined;
}

/**
 * How much of the map is drawn. `atlas` is the full treatment; `plain` keeps
 * the stations, route, headers and mascots on a neutral grey card, dropping
 * the paper tints, contours, horizons and scenery, with one accent throughout.
 */
export type AtlasVariant = 'atlas' | 'plain';

/**
 * Which glyphs the stations carry. `carved` is the map's own two-tone set;
 * `flat` is the single-colour Font Awesome set the classic list uses.
 */
export type AtlasIconStyle = 'carved' | 'flat';

/** Props for {@link AtlasRoadmap}. */
export interface AtlasRoadmapProps {
  /** Which treatment to draw. Defaults to `atlas`. */
  variant?: AtlasVariant;
  /** Which glyph set the stations carry. Defaults to `carved`. */
  icons?: AtlasIconStyle;
  /** Weeks in course order, each already marked locked or not. */
  weeks: readonly ExpeditionWeek[];
  /** Ids of completed items, across both tracks. */
  completed: ReadonlySet<string>;
  /** Called when an available station or gem is pressed. */
  onSelect: (item: ExpeditionItem) => void;
  /**
   * The course's look and feel: titles, accents, placed widgets and global
   * switches. Defaults to the stock look.
   */
  lookAndFeel?: LookAndFeel;
  /**
   * Reports the map's geometry after each layout, for an editor that overlays
   * controls on the map. When omitted, nothing is reported.
   */
  onLayout?: (info: RoadmapLayoutInfo) => void;
  /**
   * Lets the viewer press the stations and gems of locked weeks, which keep
   * their locked look. Defaults to false: locked items are disabled.
   */
  canOpenLocked?: boolean;
}

/** The atlas roadmap. */
export function AtlasRoadmap({
  weeks,
  completed,
  onSelect,
  variant = 'atlas',
  icons = 'carved',
  lookAndFeel = DEFAULT_LOOK_AND_FEEL,
  onLayout,
  canOpenLocked = false,
}: AtlasRoadmapProps) {
  const isPlain = variant === 'plain';
  const glyphs = icons === 'flat' ? FLAT_GLYPHS : CARVED_GLYPHS;
  const nextItem = findNextItem({weeks, completed});
  const linkCallout = useMapLinkCallout({onSelect});
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(CANVAS_WIDTH);
  useLayoutEffect(() => {
    const element = scrollerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    // Measured here, before the first paint, so the map is never drawn at
    // the placeholder width and then re-laid out. State set in a layout
    // effect is applied before the browser paints.
    if (element.clientWidth) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- measuring before paint is what a layout effect is for
      setContainerWidth(element.clientWidth);
    }
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setContainerWidth(width);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const g = geometryFor(
    Math.max(
      CANVAS_WIDTH,
      Math.min(MAX_CANVAS_WIDTH, Math.floor(containerWidth)),
    ),
  );
  const scale = Math.min(1, containerWidth / CANVAS_WIDTH);
  const isFullBleed = containerWidth < FULL_BLEED_BELOW;
  const layout = layoutAtlas({
    weeks,
    nextItemId: nextItem?.id,
    g,
    isPlain,
    lookAndFeel,
  });
  const lastIndex = layout.regions.length - 1;
  const layoutReport: RoadmapLayoutInfo = {
    width: layout.g.width,
    height: layout.height,
    scale,
    lands: layout.regions.map(region =>
      regionBox(
        region,
        withoutHidden(
          autoMascotPlacements(region, completed, nextItem?.id),
          weekLook(lookAndFeel, region.week.id).hiddenAuto,
        ),
      ),
    ),
  };
  const layoutKey = JSON.stringify(layoutReport);
  useEffect(() => {
    onLayout?.(layoutReport);
    // Keyed on the report itself: re-sent whenever any of it changes, not on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, onLayout]);

  return (
    <Scroller ref={scrollerRef}>
      <Frame
        $width={g.width * scale}
        $height={layout.height * scale}
        $isPlain={isPlain}
        $isFullBleed={isFullBleed}
      >
        <Canvas
          $width={g.width}
          $height={layout.height}
          $isPlain={isPlain}
          $scale={scale}
        >
          <World aria-hidden>
            <defs>
              <filter id="atlas-grain" x="0" y="0" width="1" height="1">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.85"
                  numOctaves={2}
                  stitchTiles="stitch"
                  result="noise"
                />
                <feColorMatrix
                  in="noise"
                  type="matrix"
                  values="0 0 0 0 0.3  0 0 0 0 0.25  0 0 0 0 0.2  0 0 0 0.12 0"
                />
              </filter>
            </defs>
            {!isPlain &&
              layout.regions.map(region => (
                <g key={region.week.id}>
                  {/* A whisper of the region's colour on the paper. */}
                  <rect
                    x={0}
                    y={region.top}
                    width={g.width}
                    height={region.bottom - region.top + 20}
                    fill={mix(
                      PAPER,
                      region.accent,
                      region.isLocked ? 0.02 : 0.055,
                    )}
                  />
                  <g
                    stroke={region.accent}
                    strokeWidth={0.9}
                    fill="none"
                    opacity={region.isLocked ? 0.12 : 0.2}
                  >
                    {contourPaths(region).map(({key, d}) => (
                      <path key={key} d={d} />
                    ))}
                  </g>
                  {lookAndFeel.showAutoScenery &&
                    regionScenery(region, plushSideOf(region))}
                  {region.index !== lastIndex && (
                    <path
                      d={horizonPath(region)}
                      fill="none"
                      stroke={INK}
                      strokeWidth={1}
                      opacity={0.18}
                    />
                  )}
                </g>
              ))}
            {layout.regions.map(region => (
              <g key={`deco-${region.week.id}`}>
                {svgDecorations({
                  placements: weekLook(lookAndFeel, region.week.id).decorations,
                  land: regionBox(region, []),
                  width: layout.g.width,
                  isLocked: region.isLocked,
                })}
              </g>
            ))}
            {!isPlain && (
              <rect
                width="100%"
                height="100%"
                filter="url(#atlas-grain)"
                opacity={0.7}
              />
            )}
            {layout.regions.map(region => (
              <g key={`route-${region.week.id}`}>
                {region.stations.slice(1).map((to, index) => {
                  const from = region.stations[index];
                  const isDone =
                    !region.isLocked &&
                    completed.has(from.item.id) &&
                    completed.has(to.item.id);
                  // The route leaves from below the caption under each
                  // station so it never crosses the text.
                  const d = meanderPath(
                    from.y + STATION_SIZE / 2 + UNDER_CAPTION,
                    to.y - STATION_SIZE / 2 - 6,
                    layout.meander,
                    region.anchors,
                  );
                  return (
                    <path
                      key={to.item.id}
                      d={d}
                      fill="none"
                      stroke={region.accent}
                      strokeWidth={isDone ? 3.5 : 3}
                      strokeDasharray={isDone ? undefined : '5 9'}
                      strokeLinecap="round"
                      opacity={region.isLocked ? 0.5 : isDone ? 1 : 0.7}
                    />
                  );
                })}
              </g>
            ))}
          </World>

          {layout.regions.map(region => {
            const {week, accent, isLocked} = region;
            const doneCount = countDone(week.main, completed);
            const isRegionDone = !isLocked && allDone(week.main, completed);
            const fraction = isLocked
              ? 0
              : doneCount / Math.max(1, week.main.length);
            return (
              <div key={week.id}>
                <HtmlDecorations
                  placements={[
                    ...(lookAndFeel.showMascots
                      ? withoutHidden(
                          autoMascotPlacements(region, completed, nextItem?.id),
                          weekLook(lookAndFeel, week.id).hiddenAuto,
                        )
                      : []),
                    ...weekLook(lookAndFeel, week.id).decorations,
                  ]}
                  land={regionBox(region, [])}
                  width={layout.g.width}
                  isLocked={isLocked}
                />
                {isPlain ? (
                  <Banner
                    $top={region.headerTop}
                    $height={region.headerHeight}
                    $inset={g.marginX}
                    $accent={accent}
                  >
                    <div>
                      <BannerKicker>
                        {[week.label, week.date, week.instructor]
                          .filter(Boolean)
                          .join(' · ')}
                      </BannerKicker>
                      <BannerTitle>{region.title}</BannerTitle>
                    </div>
                    <BannerPill>
                      {isLocked ? (
                        <>
                          <LockIcon /> Opens later
                        </>
                      ) : isRegionDone ? (
                        <>
                          <CheckIcon /> Complete
                        </>
                      ) : (
                        `${doneCount} / ${week.main.length}`
                      )}
                    </BannerPill>
                  </Banner>
                ) : (
                  <>
                    {numeral(week.label) && (
                      <Numeral
                        $top={region.headerTop}
                        $color={accent}
                        $inset={g.marginX}
                      >
                        {numeral(week.label)}
                      </Numeral>
                    )}
                    <Header
                      $top={region.headerTop}
                      $height={region.headerHeight}
                      $inset={g.marginX}
                    >
                      <HeaderRow>
                        <div>
                          <Kicker $color={isLocked ? INK_SOFT : accent}>
                            {[week.label, week.date, week.instructor]
                              .filter(Boolean)
                              .join(' · ')}
                          </Kicker>
                          <Title $isLocked={isLocked}>{region.title}</Title>
                        </div>
                        <Status $color={isLocked ? INK_SOFT : accent}>
                          {isLocked ? (
                            <>
                              <LockIcon />
                              <small>Opens later</small>
                            </>
                          ) : isRegionDone ? (
                            <>
                              <CheckIcon />
                              <small>Complete</small>
                            </>
                          ) : (
                            <>
                              {doneCount}
                              <small>of {week.main.length}</small>
                            </>
                          )}
                        </Status>
                      </HeaderRow>
                      <Rule $accent={accent} $fraction={fraction} />
                    </Header>
                  </>
                )}

                {region.stations.map(station => {
                  const isDone = !isLocked && completed.has(station.item.id);
                  const isNext = station.item.id === nextItem?.id;
                  const tone: StationTone = isLocked
                    ? 'locked'
                    : isDone
                      ? 'done'
                      : isNext
                        ? 'next'
                        : 'open';
                  const caption = (
                    <>
                      {isNext && <small>Up next</small>}
                      <strong>{station.item.title}</strong>
                    </>
                  );
                  return (
                    <div key={station.item.id} data-item-id={station.item.id}>
                      <StationAnchor $x={station.x} $y={station.y}>
                        {isNext && <Halo $color={accent} />}
                        <StationFace
                          type="button"
                          $accent={accent}
                          $tone={tone}
                          $size={STATION_SIZE}
                          disabled={isLocked && !canOpenLocked}
                          aria-label={station.item.title}
                          aria-pressed={
                            linkCallout.isCalloutLink(station.item)
                              ? undefined
                              : isDone
                          }
                          {...linkCallout.disclosureFor(station.item)}
                          title={itemTooltip({
                            item: station.item,
                            isLocked,
                            canOpenLocked,
                          })}
                          onClick={event =>
                            linkCallout.select(station.item, event)
                          }
                        >
                          {isLocked
                            ? glyphs.locked
                            : isDone
                              ? glyphs.done
                              : glyphs.kind[station.item.kind]}
                        </StationFace>
                      </StationAnchor>
                      <CenterCaption
                        $x={station.x}
                        $y={station.y + STATION_SIZE / 2 + 12}
                        $width={g.captionWidth}
                        $side={1}
                        $isLocked={isLocked}
                      >
                        {caption}
                      </CenterCaption>
                      {linkCallout.calloutFor({
                        item: station.item,
                        centerX: station.x,
                        top: station.y + STATION_SIZE / 2 + 10,
                        boundsWidth: g.width,
                      })}
                      {isNext && (
                        <>
                          <NextLabel
                            type="button"
                            $x={station.x}
                            $y={station.y - STATION_SIZE / 2 - 18}
                            $accent={accent}
                            onClick={() => onSelect(station.item)}
                          >
                            Continue
                          </NextLabel>
                        </>
                      )}
                    </div>
                  );
                })}

                {region.panel && (
                  <>
                    <Panel
                      $left={region.panel.left}
                      $top={region.panel.top}
                      $width={region.panel.width}
                      $height={region.panel.height}
                      $isLocked={isLocked}
                    >
                      <PanelLabel $color={isLocked ? INK_SOFT : accent}>
                        {lookAndFeel.goingFurtherLabel}
                      </PanelLabel>
                    </Panel>
                    {region.panel.gems.map(gem => {
                      const isDone = !isLocked && completed.has(gem.item.id);
                      const isLink = linkCallout.isCalloutLink(gem.item);
                      const tone: StationTone = isLocked
                        ? 'locked'
                        : isDone
                          ? 'done'
                          : 'open';
                      return (
                        <div key={gem.item.id}>
                          <StationAnchor
                            $x={gem.x}
                            $y={gem.y}
                            style={{
                              width: GEM_SIZE,
                              height: GEM_SIZE,
                              left: gem.x - GEM_SIZE / 2,
                              top: gem.y - GEM_SIZE / 2,
                            }}
                          >
                            <StationFace
                              type="button"
                              $accent={accent}
                              $tone={tone}
                              $size={GEM_SIZE}
                              disabled={isLocked && !canOpenLocked}
                              aria-label={`Going further: ${gem.item.title}`}
                              aria-pressed={isLink ? undefined : isDone}
                              {...linkCallout.disclosureFor(gem.item)}
                              title={itemTooltip({
                                item: gem.item,
                                isLocked,
                                canOpenLocked,
                              })}
                              onClick={event =>
                                linkCallout.select(gem.item, event)
                              }
                              style={
                                tone === 'open'
                                  ? {borderStyle: 'dashed'}
                                  : undefined
                              }
                            >
                              {isLocked
                                ? glyphs.locked
                                : isDone
                                  ? glyphs.done
                                  : isLink
                                    ? glyphs.kind.link
                                    : glyphs.gem}
                            </StationFace>
                          </StationAnchor>
                          <GemCaption
                            $x={gem.x}
                            $y={gem.y + GEM_SIZE / 2 + 8}
                            $isLocked={isLocked}
                          >
                            {gem.item.title}
                          </GemCaption>
                          {linkCallout.calloutFor({
                            item: gem.item,
                            centerX: gem.x,
                            top: gem.y + GEM_SIZE / 2 + 10,
                            boundsWidth: g.width,
                          })}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
          {lookAndFeel.showFinale && (
            <MapFinale
              top={layout.finale.top}
              height={layout.finale.height}
              inset={g.marginX}
              title={lookAndFeel.finaleTitle}
              subtitle={lookAndFeel.finaleSubtitle}
            />
          )}
        </Canvas>
      </Frame>
    </Scroller>
  );
}
